import { useEffect, useMemo, useState } from 'react';
import { procApi } from '../../api/procurement.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function Suppliers() {
  const [tab, setTab] = useState('contracts');
  return (
    <section>
      <header className="page-header"><h1>Supplier Management</h1></header>
      <div className="tabs">
        <button className={tab === 'contracts' ? 'tab active' : 'tab'} onClick={() => setTab('contracts')}>Contracts</button>
        <button className={tab === 'performance' ? 'tab active' : 'tab'} onClick={() => setTab('performance')}>Performance</button>
      </div>
      {tab === 'contracts' && <Contracts />}
      {tab === 'performance' && <Performance />}
    </section>
  );
}

function Contracts() {
  const { can } = useAuth();
  const canCreate = can('vendor_contract', 'create');
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);

  function refresh() { procApi.vendorContracts().then((d) => setItems(d?.items || [])); }
  useEffect(refresh, []);
  useEffect(() => {
    fetch((import.meta.env.VITE_API_BASE_URL || '/api') + '/vendors?active=true', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` },
    }).then((r) => r.json()).then((j) => setVendors(j?.data?.items || []));
  }, []);

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await procApi.createVendorContract({
      vendorId: fd.get('vendorId'),
      contractNo: fd.get('contractNo'),
      startDate: fd.get('startDate'),
      endDate: fd.get('endDate') || null,
      value: Number(fd.get('value') || 0),
      terms: fd.get('terms') || null,
    });
    e.currentTarget.reset();
    setShowForm(false);
    refresh();
  }

  return (
    <>
      {canCreate && (
        <div className="actions" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Contract'}</button>
        </div>
      )}
      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label>Vendor
            <select name="vendorId" required>
              <option value="">Select…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label>Contract #<input name="contractNo" required /></label>
          <label>Start<input name="startDate" type="date" required /></label>
          <label>End<input name="endDate" type="date" /></label>
          <label>Value<input name="value" type="number" step="0.01" defaultValue={0} /></label>
          <label className="full">Terms<textarea name="terms" rows={2} /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}
      <div className="card">
        <table className="data-table">
          <thead><tr><th>Contract #</th><th>Vendor</th><th>Period</th><th>Value</th><th>Status</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td><code>{c.contract_no}</code></td>
                <td>{c.vendor_name}</td>
                <td>{c.start_date} → {c.end_date || 'open-ended'}</td>
                <td>{c.currency} {Number(c.value).toFixed(2)}</td>
                <td><span className={`chip status-${c.status === 'active' ? 'active' : 'cancelled'}`}>{c.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No contracts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Performance() {
  const { can } = useAuth();
  const canEdit = can('vendor_performance', 'update');
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);

  function refresh() { procApi.vendorPerformance().then((d) => setItems(d?.items || [])); }
  useEffect(refresh, []);
  useEffect(() => {
    fetch((import.meta.env.VITE_API_BASE_URL || '/api') + '/vendors?active=true', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` },
    }).then((r) => r.json()).then((j) => setVendors(j?.data?.items || []));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      if (!map.has(r.vendor_id)) map.set(r.vendor_id, { vendor_name: r.vendor_name, periods: [] });
      map.get(r.vendor_id).periods.push(r);
    }
    return [...map.entries()].map(([vendor_id, g]) => ({ vendor_id, ...g }));
  }, [items]);

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await procApi.saveVendorPerformance({
      vendorId: fd.get('vendorId'),
      period: fd.get('period'),
      deliveryScore: Number(fd.get('delivery')),
      qualityScore: Number(fd.get('quality')),
      priceScore: Number(fd.get('price')),
      notes: fd.get('notes') || null,
    });
    e.currentTarget.reset();
    setShowForm(false);
    refresh();
  }

  return (
    <>
      {canEdit && (
        <div className="actions" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : 'Record Score'}</button>
        </div>
      )}
      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label>Vendor
            <select name="vendorId" required>
              <option value="">Select…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label>Period<input name="period" required placeholder="e.g. 2026-Q2" /></label>
          <label>Delivery (0-100)<input name="delivery" type="number" min={0} max={100} step="0.1" required /></label>
          <label>Quality (0-100)<input name="quality" type="number" min={0} max={100} step="0.1" required /></label>
          <label>Price (0-100)<input name="price" type="number" min={0} max={100} step="0.1" required /></label>
          <label className="full">Notes<input name="notes" /></label>
          <div className="full actions"><button className="btn primary" type="submit">Save</button></div>
        </form>
      )}

      <div className="card-grid">
        {grouped.map((g) => {
          const latest = g.periods[0];
          return (
            <div key={g.vendor_id} className="card scorecard">
              <h3>{g.vendor_name}</h3>
              <p className="muted">Latest: {latest.period}</p>
              <div className="score-row"><span>Delivery</span><Score value={latest.delivery_score} /></div>
              <div className="score-row"><span>Quality</span><Score value={latest.quality_score} /></div>
              <div className="score-row"><span>Price</span><Score value={latest.price_score} /></div>
              <div className="score-row"><span><strong>Overall</strong></span><strong>{Number(latest.overall_score).toFixed(1)}</strong></div>
            </div>
          );
        })}
        {grouped.length === 0 && <div className="muted">No performance scores recorded.</div>}
      </div>
    </>
  );
}

function Score({ value }) {
  return (
    <div className="score-bar inline">
      <div className="score-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      <span>{Number(value).toFixed(0)}</span>
    </div>
  );
}
